/**
 * SiteAssessmentDialog
 *
 * Two-tab dialog for Land Identification & Site Feasibility Analysis.
 * Called from SiteModern per-site action "Site Assessment".
 * Saves via PUT /api/sites/:site_id/assessment
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Tabs, Tab, Button, TextField, FormControl,
  InputLabel, Select, MenuItem, FormControlLabel, Switch,
  Typography, Alert, CircularProgress, Chip, Divider,
  IconButton,
} from '@mui/material';
import { X, Save, CheckCircle, XCircle, Send } from 'lucide-react';
import axios from 'axios';
import { getAuth } from 'firebase/auth';
import { usePermissions } from '../../contexts/PermissionContext';
import BASE_URL from '../../config';

const LAND_TYPES = [
  { value: 'government', label: 'Government' },
  { value: 'hr_ce', label: 'HR & CE' },
  { value: 'railway', label: 'Railway' },
  { value: 'defence', label: 'Defence' },
  { value: 'panchayat', label: 'Panchayat' },
  { value: 'school', label: 'School Campus' },
  { value: 'community', label: 'Community' },
  { value: 'riverbank', label: 'Riverbank' },
  { value: 'roadside', label: 'Roadside' },
  { value: 'degraded_forest', label: 'Degraded Forest' },
  { value: 'corporate', label: 'Corporate Campus' },
];

const FEASIBILITY_COLORS = {
  draft: 'default',
  submitted: 'warning',
  approved: 'success',
  rejected: 'error',
};

const SiteAssessmentDialog = ({ open, site, onClose, onSaved }) => {
  const { isOrgAdmin } = usePermissions();
  const [tab, setTab] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Land Identification
  const [landType, setLandType] = useState(site?.land_type || '');
  const [landDetails, setLandDetails] = useState(site?.land_details || {});

  // Feasibility
  const [feas, setFeas] = useState({
    eb_connection: false,
    fencing: '',
    water_availability: '',
    soil_conditions: '',
    notes: '',
    ...(site?.feasibility || {}),
  });

  const feasibilityStatus = site?.feasibility_status || null;

  useEffect(() => {
    if (site) {
      setLandType(site.land_type || '');
      setLandDetails(site.land_details || {});
      setFeas({
        eb_connection: false,
        fencing: '',
        water_availability: '',
        soil_conditions: '',
        notes: '',
        ...(site.feasibility || {}),
      });
    }
  }, [site]);

  const getToken = async () => {
    const user = getAuth().currentUser;
    return user ? await user.getIdToken() : null;
  };

  const save = async (action) => {
    setSaving(true);
    setError('');
    try {
      const token = await getToken();
      const res = await axios.put(
        `${BASE_URL}/api/sites/${site.site_id}/assessment`,
        {
          land_type: landType || undefined,
          land_details: Object.keys(landDetails).length ? landDetails : undefined,
          feasibility: feas,
          action,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onSaved?.(res.data.site);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const canApprove = isOrgAdmin?.() && feasibilityStatus === 'submitted';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Box>
          <Typography variant="h6">Site Assessment</Typography>
          <Typography variant="body2" color="text.secondary">{site?.name}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {feasibilityStatus && (
            <Chip
              label={feasibilityStatus.charAt(0).toUpperCase() + feasibilityStatus.slice(1)}
              size="small"
              color={FEASIBILITY_COLORS[feasibilityStatus] || 'default'}
            />
          )}
          <IconButton size="small" onClick={onClose}><X size={18} /></IconButton>
        </Box>
      </DialogTitle>

      {canApprove && (
        <Box sx={{ px: 3, pb: 1 }}>
          <Alert severity="info" sx={{ py: 0.5 }}>
            This assessment has been submitted for approval.
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Button size="small" color="success" variant="outlined" startIcon={<CheckCircle size={14} />}
                onClick={() => save('approve')} disabled={saving}>
                Approve
              </Button>
              <Button size="small" color="error" variant="outlined" startIcon={<XCircle size={14} />}
                onClick={() => save('reject')} disabled={saving}>
                Reject
              </Button>
            </Box>
          </Alert>
        </Box>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Tab label="Land Identification" />
        <Tab label="Feasibility Analysis" />
      </Tabs>

      <DialogContent sx={{ pt: 2 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {tab === 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Land Type</InputLabel>
              <Select value={landType} label="Land Type" onChange={e => setLandType(e.target.value)}>
                <MenuItem value=""><em>Not specified</em></MenuItem>
                {LAND_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
              </Select>
            </FormControl>

            {/* Conditional detail fields */}
            {landType === 'school' && (
              <>
                <TextField
                  label="School Name"
                  value={landDetails.school_name || ''}
                  onChange={e => setLandDetails(prev => ({ ...prev, school_name: e.target.value }))}
                  size="small" fullWidth
                />
                <TextField
                  label="Place"
                  value={landDetails.place || ''}
                  onChange={e => setLandDetails(prev => ({ ...prev, place: e.target.value }))}
                  size="small" fullWidth
                />
              </>
            )}

            <TextField
              label="Notes (optional)"
              value={landDetails.notes || ''}
              onChange={e => setLandDetails(prev => ({ ...prev, notes: e.target.value }))}
              multiline rows={2} size="small" fullWidth
            />
          </Box>
        )}

        {tab === 1 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Record the site's physical feasibility for plantation. This data is used in the proposal to CSR/NGO executives.
            </Typography>

            <FormControlLabel
              control={<Switch checked={!!feas.eb_connection} onChange={e => setFeas(prev => ({ ...prev, eb_connection: e.target.checked }))} />}
              label="Electricity (EB) Connection — Present"
            />

            <FormControl fullWidth size="small">
              <InputLabel>Fencing Status</InputLabel>
              <Select value={feas.fencing || ''} label="Fencing Status" onChange={e => setFeas(prev => ({ ...prev, fencing: e.target.value }))}>
                <MenuItem value=""><em>Not assessed</em></MenuItem>
                <MenuItem value="yes">Yes — fencing present</MenuItem>
                <MenuItem value="partial">Partial</MenuItem>
                <MenuItem value="no">No fencing</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Water Availability"
              value={feas.water_availability || ''}
              onChange={e => setFeas(prev => ({ ...prev, water_availability: e.target.value }))}
              placeholder="e.g. Borewell nearby, seasonal availability…"
              size="small" fullWidth
            />

            <TextField
              label="Soil Conditions"
              value={feas.soil_conditions || ''}
              onChange={e => setFeas(prev => ({ ...prev, soil_conditions: e.target.value }))}
              placeholder="e.g. Rocky terrain, loamy soil, clay…"
              multiline rows={2} size="small" fullWidth
            />

            <TextField
              label="Notes"
              value={feas.notes || ''}
              onChange={e => setFeas(prev => ({ ...prev, notes: e.target.value }))}
              multiline rows={2} size="small" fullWidth
            />

            {site?.feasibility_reviewed_at && (
              <Alert severity={feasibilityStatus === 'approved' ? 'success' : 'error'}>
                {feasibilityStatus === 'approved' ? 'Approved' : 'Rejected'} on{' '}
                {new Date(site.feasibility_reviewed_at).toLocaleDateString('en-IN')}
                {site.feasibility_reviewed_by_name && ` by ${site.feasibility_reviewed_by_name}`}
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="outlined"
          startIcon={saving ? <CircularProgress size={14} /> : <Save size={14} />}
          onClick={() => save('save_draft')}
          disabled={saving}
        >
          Save draft
        </Button>
        {tab === 1 && feasibilityStatus !== 'approved' && (
          <Button
            variant="contained"
            startIcon={<Send size={14} />}
            onClick={() => save('submit')}
            disabled={saving}
          >
            Submit for approval
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SiteAssessmentDialog;
